import { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Grid,
  Button,
  Spinner,
  Card,
  CardBody,
  IconButton,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Input,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import { Icon } from "@chakra-ui/react";
import { Upload, X } from "lucide-react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
  "image/webp",
] as const;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_ATTACHMENT_FILES = 10;
const ACCEPT_ATTR = "image/jpeg,image/png,image/gif,image/webp,.jpeg,.jpg,.png,.gif,.webp";

/** If date is ISO string, parse and return yyyy-mm-dd in local time; otherwise return as-is. */
function normalizeApiDate(dateRaw: unknown): string {
  if (dateRaw == null) return "";
  if (typeof dateRaw !== "string") return "";
  const s = dateRaw.trim();
  if (!s) return "";
  if (s.includes("T")) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.substring(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return s.length >= 10 ? s.substring(0, 10) : s;
}

interface DownloadBillingAndSoaFormProps {
  shippingLineId: string;
  shippingLineName: string;
  soaId?: number;
  billingStatementId?: number;
  onSuccess?: () => void;
  onCancel: () => void;
}

const DOWNLOAD_REVOKE_DELAY_MS = 500;

export function DownloadBillingAndSoaForm({
  shippingLineId,
  shippingLineName,
  soaId,
  billingStatementId,
  onSuccess,
  onCancel,
}: DownloadBillingAndSoaFormProps) {
  const colors = useThemeColors();
  const { showError, showSuccess } = useToastNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [soaDisplay, setSoaDisplay] = useState<{
    dli_sa_number: string;
    work_order: string;
  }>({ dli_sa_number: "", work_order: "" });
  const [isLoadingBilling, setIsLoadingBilling] = useState(false);
  const [billingDisplay, setBillingDisplay] = useState({
    billingStatementNo: "",
    billedTo: "",
    paymentTerm: "",
    ciDate: "",
    dueDate: "",
    busStyle: "",
    hasDetails: false,
  });
  const isMountedRef = useRef(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Attachment images for PDF (optional)
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  // Guard: soaId and billingStatementId must be valid
  const safeSoaId = typeof soaId === "number" && Number.isFinite(soaId) ? soaId : null;
  const safeBillingStatementId = typeof billingStatementId === "number" && Number.isFinite(billingStatementId) ? billingStatementId : null;

  const hasAttachments = attachmentFiles.length > 0;

  function validateAttachmentFile(file: File): string | null {
    const type = file.type?.toLowerCase() ?? "";
    if (!ALLOWED_IMAGE_TYPES.includes(type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
      return `"${file.name}": must be JPEG, PNG, JPG, GIF, or WebP.`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `"${file.name}": must be 5MB or less.`;
    }
    return null;
  }

  function handleAttachmentChange(e: React.ChangeEvent<HTMLInputElement>) {
    setAttachmentError(null);
    const input = e.target;
    const fileList = input.files;
    if (!fileList || fileList.length === 0) return;

    const next: File[] = [];
    const errors: string[] = [];
    const total = Math.min(fileList.length, MAX_ATTACHMENT_FILES + (fileList.length > MAX_ATTACHMENT_FILES ? 1 : 0));
    for (let i = 0; i < fileList.length; i++) {
      if (next.length >= MAX_ATTACHMENT_FILES) {
        errors.push(`Maximum ${MAX_ATTACHMENT_FILES} files allowed.`);
        break;
      }
      const file = fileList[i];
      const err = validateAttachmentFile(file);
      if (err) errors.push(err);
      else next.push(file);
    }
    if (errors.length > 0) {
      setAttachmentError(errors.slice(0, 3).join(" "));
    }
    setAttachmentFiles((prev) => {
      const combined = [...prev, ...next].slice(0, MAX_ATTACHMENT_FILES);
      return combined;
    });
    input.value = "";
  }

  function removeAttachment(index: number) {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  }

  // Fetch SOA data for display
  useEffect(() => {
    if (safeSoaId == null) return;

    isMountedRef.current = true;
    const fetchSoaData = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/soa/${safeSoaId}`);
        const soaData = response.data?.data ?? response.data ?? {};
        if (isMountedRef.current) {
          setSoaDisplay({
            dli_sa_number: soaData.dli_sa_number ?? "",
            work_order: soaData.work_order ?? "",
          });
        }
      } catch (error: unknown) {
        if (isMountedRef.current) {
          showError(error as Error);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchSoaData();
    return () => {
      isMountedRef.current = false;
    };
  }, [safeSoaId, showError]);

  // Fetch billing statement data for display when billingStatementId is provided
  useEffect(() => {
    if (safeBillingStatementId == null) return;

    isMountedRef.current = true;
    const fetchBillingStatement = async () => {
      setIsLoadingBilling(true);
      try {
        const response = await apiClient.get(`/billing-statements/${safeBillingStatementId}`);
        const data = response.data?.data ?? response.data ?? {};
        const billingStatementNo =
          data.billing_statement_no ?? (data as Record<string, unknown>).billing_statement_no ?? "";
        const soa = data.statement_of_account ?? (data as Record<string, unknown>).statement_of_account;
        const shippingLine = data.shipping_line ?? soa?.shipping_line ?? (soa as Record<string, unknown>)?.shipping_line;
        const billedTo =
          shippingLine?.name ?? (shippingLine as Record<string, unknown>)?.name ?? "";
        const paymentTerm = data.payment_term ?? (data as Record<string, unknown>).payment_term ?? "";
        const ciDateRaw = data.ci_date ?? (data as Record<string, unknown>).ci_date;
        const dueDateRaw = data.due_date ?? (data as Record<string, unknown>).due_date;
        const busStyle = data.bus_style ?? (data as Record<string, unknown>).bus_style ?? "";
        const hasDetails = data.has_details ?? (data as Record<string, unknown>).has_details ?? false;

        if (isMountedRef.current) {
          setBillingDisplay({
            billingStatementNo: String(billingStatementNo),
            billedTo: String(billedTo),
            paymentTerm: String(paymentTerm),
            ciDate: normalizeApiDate(ciDateRaw),
            dueDate: normalizeApiDate(dueDateRaw),
            busStyle: String(busStyle),
            hasDetails: hasDetails === true,
          });
        }
      } catch (error: unknown) {
        if (isMountedRef.current) {
          showError(error as Error);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingBilling(false);
        }
      }
    };

    fetchBillingStatement();
    return () => {
      isMountedRef.current = false;
    };
  }, [safeBillingStatementId, showError]);

  const handleDownload = async (format: "pdf" | "csv") => {
    if (safeSoaId == null) {
      showError(new Error("SOA ID is required to download."));
      return;
    }

    const setIsDownloading =
      format === "pdf" ? setIsDownloadingPdf : setIsDownloadingCsv;
    const downloadUrl =
      format === "pdf"
        ? `/soa/${safeSoaId}/download-billing-and-soa`
        : `/soa/${safeSoaId}/download-billing-and-soa-csv`;
    const fileExtension = format === "pdf" ? "pdf" : "csv";
    const successMessage =
      format === "pdf"
        ? "SOA and Billing Statement PDF downloaded successfully."
        : "SOA and Billing Statement CSV downloaded successfully.";
    const baseFilename = `${billingDisplay.billingStatementNo || "Billing Statement"} + ${soaDisplay.dli_sa_number || "SOA"}`;

    setIsDownloading(true);
    let objectUrl: string | null = null;
    const includeAttachments = hasAttachments;

    try {
      // When there are attachments, upload first; only proceed to download if upload succeeds
      if (includeAttachments && attachmentFiles.length > 0) {
        setIsUploadingAttachments(true);
        try {
          const formData = new FormData();
          attachmentFiles.forEach((file) => {
            formData.append("images[]", file, file.name);
          });
          await apiClient.request({
            method: "POST",
            url: "/soa-and-billing/attachments",
            data: formData,
            timeout: 60000,
            headers: {
              "Content-Type": undefined,
            },
          });
        } catch (uploadError: unknown) {
          if (isMountedRef.current) {
            showError(uploadError as Error);
          }
          return;
        } finally {
          if (isMountedRef.current) {
            setIsUploadingAttachments(false);
          }
        }
        if (!isMountedRef.current) return;
      }

      const response = await apiClient.get(downloadUrl, {
        responseType: "blob",
        timeout: 60000,
        params: { include_attachments: includeAttachments },
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("Invalid response: expected a file.");
      }

      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.setAttribute("download", `${baseFilename}.${fileExtension}`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Revoke after delay so the browser has time to start the download (avoids memory leak)
      window.setTimeout(() => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      }, DOWNLOAD_REVOKE_DELAY_MS);

      if (isMountedRef.current) {
        showSuccess("Success", successMessage);
        onSuccess?.();
      }
    } catch (error: unknown) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      if (isMountedRef.current) {
        showError(error as Error);
      }
    } finally {
      if (isMountedRef.current) {
        setIsDownloading(false);
        setIsUploadingAttachments(false);
      }
    }
  };

  if (safeSoaId == null) {
    return (
      <Box p={4}>
        <Text color={colors.textSecondary}>Invalid SOA. Cannot load form.</Text>
        <Button mt={4} onClick={onCancel} variant="outline" size="sm">
          Cancel
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color={colors.text}>
          Loading SOA data...
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Display-only section: labels only */}
      <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Grid
              templateColumns={{ base: "1fr", md: "3fr 9fr" }}
              gap={4}
              alignItems="start"
            >
              <Text fontWeight="medium" color={colors.text}>
                Shipping Line:
              </Text>
              <Text color={colors.text} bg={colors.sectionBg} px={3} py={2} borderRadius="md">
                {shippingLineName || "-"}
              </Text>
            </Grid>
            <Grid
              templateColumns={{ base: "1fr", md: "3fr 9fr" }}
              gap={4}
              alignItems="start"
            >
              <Text fontWeight="medium" color={colors.text}>
                DLI-SA Number:
              </Text>
              <Text color={colors.text} bg={colors.sectionBg} px={3} py={2} borderRadius="md">
                {soaDisplay.dli_sa_number || "-"}
              </Text>
            </Grid>
            <Grid
              templateColumns={{ base: "1fr", md: "3fr 9fr" }}
              gap={4}
              alignItems="start"
            >
              <Text fontWeight="medium" color={colors.text}>
                Work Order Number:
              </Text>
              <Text color={colors.text} bg={colors.sectionBg} px={3} py={2} borderRadius="md">
                {soaDisplay.work_order || "-"}
              </Text>
            </Grid>
          </VStack>
        </CardBody>
      </Card>

      {/* Billing Statement read-only (same fields as DownloadBillingForm); only when billingStatementId is provided */}
      {safeBillingStatementId != null && (
        <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
          <CardBody>
            {isLoadingBilling ? (
              <Box py={6} textAlign="center">
                <Spinner size="lg" color="blue.500" />
                <Text mt={4} color={colors.text}>
                  Loading billing statement...
                </Text>
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                <Grid
                  templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    Billed To:
                  </FormLabel>
                  <FormControl>
                    <Input
                      value={billingDisplay.billedTo}
                      readOnly
                      bg={colors.sectionBg}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    Billing Statement No:
                  </FormLabel>
                  <FormControl>
                    <Input
                      value={billingDisplay.billingStatementNo}
                      readOnly
                      bg={colors.sectionBg}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    Payment Term:
                  </FormLabel>
                  <FormControl>
                    <Input
                      value={billingDisplay.paymentTerm}
                      readOnly
                      bg={colors.sectionBg}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormControl>
                    <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                      CI Date:
                    </FormLabel>
                    <Input
                      value={billingDisplay.ciDate}
                      readOnly
                      bg={colors.sectionBg}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                      Due Date:
                    </FormLabel>
                    <Input
                      value={billingDisplay.dueDate}
                      readOnly
                      bg={colors.sectionBg}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                  gap={4}
                  alignItems="flex-end"
                >
                  <FormControl>
                    <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                      Business Style:
                    </FormLabel>
                    <Input
                      value={billingDisplay.busStyle}
                      readOnly
                      bg={colors.sectionBg}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                      Has Details
                    </FormLabel>
                    <Input
                      value={billingDisplay.hasDetails ? "Yes" : "No"}
                      readOnly
                      bg={colors.sectionBg}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                  </FormControl>
                </Grid>
              </VStack>
            )}
          </CardBody>
        </Card>
      )}

      {/* Attachments (optional): images for PDF */}
      <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
        <CardBody>
          <FormControl isInvalid={!!attachmentError}>
            <VStack align="stretch" spacing={3}>
              <FormLabel fontWeight="medium" color={colors.text}>
                Attachments (optional)
              </FormLabel>
              <FormHelperText color={colors.textSecondary} mt={0}>
                JPEG, PNG, JPG, GIF, or WebP. Max 5MB each. Max {MAX_ATTACHMENT_FILES} files.
              </FormHelperText>
              <HStack spacing={2} flexWrap="wrap">
                <Button
                  size="sm"
                  leftIcon={<Icon as={Upload} boxSize={4} />}
                  onClick={() => fileInputRef.current?.click()}
                  isDisabled={attachmentFiles.length >= MAX_ATTACHMENT_FILES}
                  variant="outline"
                  borderColor={colors.border}
                  color={colors.text}
                >
                  Choose images
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  multiple
                  onChange={handleAttachmentChange}
                  style={{ display: "none" }}
                  aria-hidden
                />
                {attachmentFiles.length > 0 && (
                  <Text fontSize="sm" color={colors.textSecondary}>
                    {attachmentFiles.length} file(s) selected
                  </Text>
                )}
              </HStack>
              <FormErrorMessage>{attachmentError ?? ""}</FormErrorMessage>
              {attachmentFiles.length > 0 && (
              <Wrap spacing={2}>
                {attachmentFiles.map((file, index) => (
                  <WrapItem key={`${file.name}-${index}`}>
                    <Tag size="md" colorScheme="blue" borderRadius="full" gap={1}>
                      <TagLabel maxW="120px" noOfLines={1} title={file.name}>
                        {file.name}
                      </TagLabel>
                      <IconButton
                        aria-label={`Remove ${file.name}`}
                        size="xs"
                        variant="ghost"
                        icon={<Icon as={X} boxSize={3} />}
                        onClick={() => removeAttachment(index)}
                        minW={6}
                        minH={6}
                      />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
            </VStack>
          </FormControl>
        </CardBody>
      </Card>

      {/* Action Buttons: Cancel, Download PDF, Download CSV */}
      <HStack spacing={{ base: 2, md: 4 }} justify="flex-end" flexWrap="wrap" pt={2}>
        <Button
          variant="outline"
          onClick={onCancel}
          borderColor={colors.border}
          color={colors.text}
          size={{ base: "xs", sm: "sm", md: "md" }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          px={{ base: 2, sm: 3, md: 4 }}
          whiteSpace="nowrap"
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleDownload("pdf")}
          colorScheme="blue"
          isLoading={isDownloadingPdf}
          loadingText={isUploadingAttachments ? "Uploading..." : "Downloading..."}
          isDisabled={
            safeSoaId == null || isDownloadingCsv || isUploadingAttachments
          }
          size={{ base: "xs", sm: "sm", md: "md" }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          px={{ base: 2, sm: 3, md: 4 }}
          whiteSpace="nowrap"
        >
          Download PDF
        </Button>
        <Button
          onClick={() => handleDownload("csv")}
          colorScheme="blue"
          isLoading={isDownloadingCsv}
          loadingText={isUploadingAttachments ? "Uploading..." : "Downloading..."}
          isDisabled={
            safeSoaId == null || isDownloadingPdf || isUploadingAttachments
          }
          size={{ base: "xs", sm: "sm", md: "md" }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          px={{ base: 2, sm: 3, md: 4 }}
          whiteSpace="nowrap"
        >
          Download CSV
        </Button>
      </HStack>
    </VStack>
  );
}
